import { ApolloError, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import { NotifyOrganizationOwnerInvitation } from 'Apollo';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { useCallback, useEffect, useState } from 'react';
import { parseEmailTokens } from 'Tools/organizationInviteEmail';

const MESSAGE_MAX_LENGTH = 1000;

export type OrganizationInviteNotifyModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Code compte cible pour la mutation (déjà résolu côté parent). */
  readonly accountCode: string;
};

export function OrganizationInviteNotifyModal({
  open,
  onClose,
  accountCode
}: OrganizationInviteNotifyModalProps) {
  const ts = useTranslate();
  const { onError, onSuccess } = useNotification();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();

  const [notify, { loading }] = useMutation(NotifyOrganizationOwnerInvitation);

  useEffect(() => {
    if (open) {
      setEmail('');
      setMessage('');
      setEmailError(undefined);
    }
  }, [open]);

  const verifyEmail = useCallback((value: string) => {
    const regex = AppSettings.EMAIL_VALIDATION;
    return new RegExp(regex).test(value);
  }, []);

  const validateEmailField = (): boolean => {
    const tokens = parseEmailTokens(email);
    if (tokens.length > 1) {
      setEmailError(
        ts('page.dashBoard.organizationInvite.notify.multiEmailError')
      );
      return false;
    }
    if (tokens.length === 0) {
      setEmailError(ts('error.form.requiredField'));
      return false;
    }
    if (!verifyEmail(tokens[0])) {
      setEmailError(ts('error.form.invalidEmail'));
      return false;
    }
    setEmailError(undefined);
    return true;
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSend = async () => {
    if (!validateEmailField()) {
      return;
    }
    const tokens = parseEmailTokens(email);
    const trimmedMessage = message.trim();
    try {
      await notify({
        variables: {
          accountCode,
          inviteeEmail: tokens[0],
          message: trimmedMessage.length > 0 ? trimmedMessage : null
        }
      });
      onSuccess('page.dashBoard.organizationInvite.notify.success');
      onClose();
    } catch (e) {
      onError(e as ApolloError);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {ts('page.dashBoard.organizationInvite.notify.modalTitle')}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {ts('page.dashBoard.organizationInvite.notify.infoBanner')}
        </Alert>
        <TextField
          fullWidth
          margin="dense"
          label={ts('page.dashBoard.organizationInvite.notify.emailLabel')}
          placeholder={ts(
            'page.dashBoard.organizationInvite.notify.emailPlaceholder'
          )}
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            if (emailError) {
              setEmailError(undefined);
            }
          }}
          onBlur={() => {
            if (email.trim()) {
              validateEmailField();
            }
          }}
          error={!!emailError}
          helperText={emailError}
          disabled={loading}
        />
        <TextField
          fullWidth
          margin="normal"
          multiline
          minRows={4}
          inputProps={{ maxLength: MESSAGE_MAX_LENGTH }}
          label={ts('page.dashBoard.organizationInvite.notify.messageLabel')}
          placeholder={ts(
            'page.dashBoard.organizationInvite.notify.messagePlaceholder'
          )}
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {ts('page.dashBoard.organizationInvite.notify.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loading || !email.trim()}
        >
          {ts('page.dashBoard.organizationInvite.notify.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
